import React from 'react';
import styled from 'styled-components';
import { fontFamilyBold, text } from '../styling/mixins/typography';
import { getColor } from '../styling/mixins/colors';
import Icon from './Icon';

interface StyledPaginationLinkProps {
  $current: boolean;
  $disabled: boolean;
}

const StyledPagination = styled.ul`
  display: flex;
  justify-content: center;

  .pagination__link {
    ${text('medium')}
    ${fontFamilyBold}
    width: 40px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;

    &--current {
      background-color: ${getColor('grey', 100)};
    }
  }
`;

const StyledPaginationLink = styled.a<StyledPaginationLinkProps>`
  ${text('medium')}
  ${fontFamilyBold}
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background-color: ${(p) => (p.$current ? getColor('grey', 200) : 'none')};
  cursor: ${(p) => (p.$current ? 'default' : 'pointer')};
  pointer-events: ${(p) => (p.$disabled ? 'none' : 'auto')};
`;

interface StyledPaginationIconProps {
  $disabled: boolean;
}

const StyledPaginationIcon = styled(Icon)<StyledPaginationIconProps>`
  font-size: 2rem;
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  pointer-events: ${(p) => (p.$disabled ? 'none' : 'auto')};
  opacity: ${(p) => (p.$disabled ? '0.25' : '1')};
`;

interface PaginationItemProps {
  name?: string | number;
  icon?: string;
  current?: boolean;
  disabled?: boolean;
  onChange?: () => void;
}

const PaginationItem = ({
  name = '',
  icon = '',
  current = false,
  disabled = false,
  onChange,
}: PaginationItemProps) => {
  return (
    <li className="pagination-item">
      <StyledPaginationLink
        $current={current}
        $disabled={disabled}
        onClick={() => onChange && onChange()}
      >
        {name ? name : null}
        {icon ? <StyledPaginationIcon name={icon} $disabled={disabled} /> : null}
      </StyledPaginationLink>
    </li>
  );
};

const RenderFirstHalfItems = ({
  intervalStart,
  edgeEntries,
  currentPageIndex,
  onChange,
}) => {
  const renderItems: JSX.Element[] = [];
  const end = Math.min(edgeEntries, intervalStart);
  for (let i = 0; i < end; i++) {
    const publicPageIndex = i + 1;
    renderItems.push(
      <PaginationItem
        key={publicPageIndex}
        name={publicPageIndex}
        current={publicPageIndex === currentPageIndex}
        onChange={() => onChange(publicPageIndex)}
      />
    );
  }
  if (edgeEntries < intervalStart) {
    renderItems.push(
      <PaginationItem key="first_indicator" name="..." disabled={true} />
    );
  }
  return <>{renderItems}</>;
};

const RenderMiddleItems = ({
  intervalStart,
  intervalEnd,
  currentPageIndex,
  onChange,
}) => {
  const renderItems: JSX.Element[] = [];
  for (let i = intervalStart; i < intervalEnd; i++) {
    const publicPageIndex = i + 1;
    renderItems.push(
      <PaginationItem
        key={publicPageIndex}
        name={publicPageIndex}
        current={publicPageIndex === currentPageIndex}
        onChange={() => onChange(publicPageIndex)}
      />
    );
  }
  return <>{renderItems}</>;
};

const RenderSecondHalfItems = ({
  intervalEnd,
  edgeEntries,
  pageCount,
  currentPageIndex,
  onChange,
}) => {
  const renderItems: JSX.Element[] = [];
  if (pageCount - edgeEntries > intervalEnd) {
    renderItems.push(
      <PaginationItem key="second_indicator" name="..." disabled={true} />
    );
  }
  const begin = Math.max(pageCount - edgeEntries, intervalEnd);
  for (let i = begin; i < pageCount; i++) {
    const publicPageIndex = i + 1;
    renderItems.push(
      <PaginationItem
        key={publicPageIndex}
        name={publicPageIndex}
        current={publicPageIndex === currentPageIndex}
        onChange={() => onChange(publicPageIndex)}
      />
    );
  }
  return <>{renderItems}</>;
};

const Pagination = ({ model, onChange }) => {
  const {
    currentPageIndex,
    pageCount,
    intervalStart,
    intervalEnd,
    edgeEntries,
  } = model;

  return (
    <>
      {pageCount > 1 && (
        <StyledPagination>
          <PaginationItem
            icon="chevron-left"
            current={false}
            disabled={currentPageIndex <= 1}
            onChange={() => onChange(currentPageIndex - 1)}
          />
          {intervalStart > 0 && edgeEntries > 0 && (
            <RenderFirstHalfItems
              intervalStart={intervalStart}
              edgeEntries={edgeEntries}
              currentPageIndex={currentPageIndex}
              onChange={onChange}
            />
          )}
          <RenderMiddleItems
            intervalStart={intervalStart}
            intervalEnd={intervalEnd}
            currentPageIndex={currentPageIndex}
            onChange={onChange}
          />
          {intervalEnd < pageCount && edgeEntries > 0 && (
            <RenderSecondHalfItems
              intervalEnd={intervalEnd}
              pageCount={pageCount}
              edgeEntries={edgeEntries}
              currentPageIndex={currentPageIndex}
              onChange={onChange}
            />
          )}
          <PaginationItem
            icon="chevron-right"
            current={false}
            disabled={currentPageIndex >= pageCount}
            onChange={() => onChange(currentPageIndex + 1)}
          />
        </StyledPagination>
      )}
    </>
  );
};

export default Pagination;
